import { action, computed, extendObservable, flow, IObservableArray, observable, toJS } from 'mobx';
import Rx from 'stores/Rx';
import type RootStore from 'stores/RootStore';
import type { OrderBy, ScriptCounts, WhereFilter } from 'types/common';
import Query from 'utilities/Query';
import type { AssignableUser } from 'stores/Account';
import API from 'api';

type View = 'all' | 'my';
export type DashboardRx = Rx | { checked: boolean };

export default class Dashboard {
	rootStore: RootStore = null;

	constructor(rootStore) {
		this.rootStore = rootStore;
	}

	@observable view: View = 'all';
	@observable therapeuticAreaID: number = 0;
	@observable scriptCountAll: ScriptCounts = {};
	@observable scriptCountMy: ScriptCounts = {};
	@observable statusFilter: string = 'pending';
	@observable pageSize: number = 100;
	@observable items: IObservableArray<DashboardRx> = [];
	@observable pageIndex: number = 0;
	@observable pageSize: number = 100;
	@observable itemCount: number = 0;
	@observable filters: IObservableArray<WhereFilter> = [];
	@observable orderBy: OrderBy = { field: 'id', asc: false };
	@observable loadingCounter: number = 0;
	@observable assignableUsers: IObservableArray<AssignableUser> = [];
	@observable assignToUser: AssignableUser = null;

	extend(data: JSON) {
		Object.assign(this, data);
	}

	loadItems = flow(
		function*(filters?: Array<WhereFilter> = []) {
			try {
				this.loadingCounter++;
				if (this.statusFilter && this.statusFilter !== 'all') {
					filters.push({ field: 'status', value: this.statusFilter });
				}
				if (this.therapeuticAreaID) {
					filters.push({ field: 'searchTherapeuticAreaID', value: this.therapeuticAreaID });
				}
				filters = filters.concat(toJS(this.filters));
				let query = new Query(filters, this.orderBy, this.pageIndex, this.pageSize);

				let rxs = yield Rx.load(query, this.therapeuticArea);

				this.items = rxs.items.map(el =>
					extendObservable(el, { checked: this.statusFilter === 'pending' ? true : false })
				);
				this.itemCount = rxs.itemCount;
			} finally {
				this.loadingCounter--;
			}
		}.bind(this)
	);

	loadScriptCounts = flow(
		function*() {
			try {
				this.loadingCounter++;
				let counts = yield Rx.getCountByUserArea(this.therapeuticAreaID);
				this.scriptCountAll = counts.allScripts;
				this.scriptCountMy = counts.myScripts;
			} finally {
				this.loadingCounter--;
			}
		}.bind(this)
	);

	@action
	reload = () => {
		this.loadItems();
		this.loadScriptCounts();
	};

	@action
	setTherapeuticAreaID(value) {
		this.therapeuticAreaID = value;
		this.pageIndex = 0;
		this.loadItems();
		this.loadScriptCounts();
	}

	@action
	setStatusFilter(newStatus) {
		this.statusFilter = newStatus;
		this.pageIndex = 0;
		this.loadItems();
	}

	@action
	setPageIndex(idx: number) {
		this.pageIndex = idx;
		this.loadItems();
	}

	@action
	setPageSize(idx: number) {
		this.pageSize = idx;
		this.loadItems();
	}

	@action
	clearSelectedScripts() {
		this.selectedScripts.clear();
	}

	@action
	toggleCheckboxes() {
		let allSelected = this.items.every(el => el.checked);
		if (allSelected) {
			this.items.forEach(el => {
				el.checked = false;
			});
		} else {
			this.items.forEach(el => {
				el.checked = true;
			});
		}
	}

	@action
	addFilter(filter: WhereFilter) {
		this.filters = this.filters.filter(f => f.field !== filter.field);
		this.filters.push(filter);
		this.pageIndex = 0;
		this.loadItems();
	}

	@action
	addFilters(filter: Array<WhereFilter>) {
		const fields = filter.map(f => f.field);
		this.filters = this.filters.filter(f => !fields.includes(f.field));
		filter.forEach(f => this.filters.push(f));
		this.pageIndex = 0;
		this.loadItems();
	}

	@action
	removeFilter(field: string) {
		let fieldToRemove = this.filters.find(el => el.field === field);
		if (fieldToRemove) {
			this.filters.remove(fieldToRemove);
			this.pageIndex = 0;
			this.loadItems();
		}
	}

	@action
	setOrderBy(field: string) {
		if (this.orderBy.field === field) {
			this.orderBy.asc = !this.orderBy.asc;
		} else {
			this.orderBy.field = field;
			this.orderBy.asc = true;
		}
		this.loadItems();
	}

	@computed
	get isLoading(): boolean {
		return this.loadingCounter > 0;
	}

	@computed
	get scriptCount(): ScriptCounts {
		return this.view === 'all' ? this.scriptCountMy : this.scriptCountMy;
	}

	@computed
	get numberOfPages(): number {
		return Math.ceil(this.itemCount / this.pageSize);
	}

	@computed
	get selectedScripts(): IObservableArray<DashboardRx> {
		return this.items.filter(el => el.checked);
	}
}



// WEBPACK FOOTER //
// ./src/stores/ui/manager/Dashboard.js